import {
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Button,
  Radio,
} from '@mui/material'
import { useMemo, useRef, useState, useEffect } from 'react'
import './Bundle.css'
import { lineItemType, shipToType } from '../types'
import { Close, DeleteOutlined } from '@mui/icons-material'
import dayjs from 'dayjs'

const COLUMN_WIDTHS = {
  Radio: '80px',
  Number: '80px',
  Supplier: '250px',
  MfrPartNumber: '175px',
  Manufacturer: '250px',
  SupplierPartNumber: '250px',
  Description: '250px',
  ShipToAddress: '250px',
  Qty: '150px',
  UnitCost: '150px',
  ExtCost: '150px',
  UnitPrice: '150px',
  ExtPrice: '150px',
  TAACompliant: '250px',
  ProductCategory: '250px',
  CountryOfOrigin: '250px',
  PoPStart: '250px',
  PoPEnd: '250px',
  ServiceTag: '250px',
}

export function mergeLinesForBundle(header: lineItemType, children: lineItemType[]): lineItemType {
  let productDescription = header.ProductDescription ?? ''
  let unitPrice = header.UnitPrice ?? 0
  let unitCost = header.UnitCost ?? 0

  for (const child of children) {
    const childDesc = child.ProductDescription ?? ''
    const partNum = child.SupplierPartNum ?? ''
    const descWithPart = childDesc + (childDesc.includes(partNum) ? '' : ` ${partNum}`).trim()
    productDescription += `,\n${descWithPart}`
    unitPrice += child.UnitPrice ?? 0
    unitCost += child.UnitCost ?? 0
  }

  const quantity = header.Quantity ?? 0
  const extPrice = unitPrice * quantity
  const extCost = unitCost * quantity

  return {
    ...header,
    ProductDescription: productDescription || null,
    UnitPrice: unitPrice,
    ExtPrice: extPrice,
    UnitCost: unitCost,
    ExtCost: extCost,
    Bundle: true,
  }
}

export default function Bundle({
  open,
  setOpen,
  selectedLineItems,
  setSelectedLineItems,
  onCreateBundle,
  shippingAddresses = [],
}: {
  open: boolean
  setOpen: (open: boolean) => void
  selectedLineItems: lineItemType[]
  setSelectedLineItems: (lineItems: lineItemType[]) => void
  onCreateBundle?: (mergedLine: lineItemType) => void
  shippingAddresses?: (shipToType & { default: boolean; id: number })[]
}) {
  const tableContainerRef = useRef<HTMLDivElement | null>(null)
  const stickyScrollbarRef = useRef<HTMLDivElement | null>(null)
  const syncingScrollRef = useRef(false)
  const [stickyScrollbarWidth, setStickyScrollbarWidth] = useState(0)
  const [showStickyScrollbar, setShowStickyScrollbar] = useState(false)
  const [headerLine, setHeaderLine] = useState<lineItemType | null>(null)

  const defaultAddress = useMemo(() => shippingAddresses.find(address => address.default), [shippingAddresses])

  const canCreateBundle = selectedLineItems.length >= 2 && headerLine !== null

  const handleCreateBundle = () => {
    if (!headerLine || selectedLineItems.length < 2) return
    const children = selectedLineItems.filter(item => item.Number !== headerLine.Number)
    const mergedLine = mergeLinesForBundle(headerLine, children)
    onCreateBundle?.(mergedLine)
    setOpen(false)
    setHeaderLine(null)
  }

  const syncScroll = (source: 'table' | 'sticky') => {
    const tableEl = tableContainerRef.current
    const stickyEl = stickyScrollbarRef.current
    if (!tableEl || !stickyEl) return
    if (syncingScrollRef.current) return

    syncingScrollRef.current = true
    if (source === 'table') stickyEl.scrollLeft = tableEl.scrollLeft
    else tableEl.scrollLeft = stickyEl.scrollLeft
    requestAnimationFrame(() => {
      syncingScrollRef.current = false
    })
  }

  useEffect(() => {
    if (!tableContainerRef.current) return

    const update = () => {
      if (!tableContainerRef.current) return

      setStickyScrollbarWidth(tableContainerRef.current.scrollWidth)
      setShowStickyScrollbar(tableContainerRef.current.scrollWidth > tableContainerRef.current.clientWidth)

      const stickyEl = stickyScrollbarRef.current
      if (stickyEl) stickyEl.scrollLeft = tableContainerRef.current.scrollLeft
    }

    update()

    window.addEventListener('resize', update)

    const ro = new ResizeObserver(() => update())
    ro.observe(tableContainerRef.current)

    return () => {
      window.removeEventListener('resize', update)
      ro.disconnect()
    }
  }, [selectedLineItems.length])

  return (
    <Dialog open={open} fullScreen className="bundle-dialog">
      <DialogTitle className="bundle-title">
        <Typography variant="h6">Create Bundle</Typography>
        <div className="bundle-title-buttons">
          {!canCreateBundle && selectedLineItems.length > 0 && (
            <Typography variant="body2" color="text.secondary">
              Select a header (parent) and at least one other line.
            </Typography>
          )}
          <Button
            variant="contained"
            onClick={onCreateBundle ? handleCreateBundle : () => setOpen(false)}
            disabled={onCreateBundle ? !canCreateBundle : false}
          >
            Create Bundle
          </Button>
          <IconButton
            onClick={() => {
              setOpen(false)
              setHeaderLine(null)
            }}
          >
            <Close />
          </IconButton>
        </div>
      </DialogTitle>
      <DialogContent className="bundle-dialog-content">
        <TableContainer className="bundle-table" ref={tableContainerRef} onScroll={() => syncScroll('table')}>
          <Table color="primary">
            <TableHead>
              <TableRow>
                <TableCell width={COLUMN_WIDTHS.Radio} className="bundle-radio-column" align="center" />
                <TableCell
                  width={COLUMN_WIDTHS.Number}
                  className="bundle-number-column"
                  sx={{ left: COLUMN_WIDTHS.Radio }}
                >
                  #
                </TableCell>
                <TableCell
                  width={COLUMN_WIDTHS.Supplier}
                  className="bundle-supplier-column"
                  sx={{ left: `calc(${COLUMN_WIDTHS.Radio} + ${COLUMN_WIDTHS.Number})` }}
                >
                  Supplier
                </TableCell>
                <TableCell
                  width={COLUMN_WIDTHS.MfrPartNumber}
                  className="bundle-mfr-part-number-column"
                  sx={{
                    left: `calc(${COLUMN_WIDTHS.Radio} + ${COLUMN_WIDTHS.Number} + ${COLUMN_WIDTHS.Supplier})`,
                  }}
                >
                  Mfr Part #
                </TableCell>
                <TableCell width={COLUMN_WIDTHS.Manufacturer}>Manufacturer</TableCell>
                <TableCell width={COLUMN_WIDTHS.SupplierPartNumber}>Supplier Part #</TableCell>
                <TableCell width={COLUMN_WIDTHS.Description}>Description</TableCell>
                <TableCell width={COLUMN_WIDTHS.ShipToAddress}>Ship to Address</TableCell>
                <TableCell width={COLUMN_WIDTHS.Qty}>Qty</TableCell>
                <TableCell width={COLUMN_WIDTHS.UnitCost}>Unit Cost</TableCell>
                <TableCell width={COLUMN_WIDTHS.ExtCost}>Ext Cost</TableCell>
                <TableCell width={COLUMN_WIDTHS.UnitPrice}>Unit Price</TableCell>
                <TableCell width={COLUMN_WIDTHS.ExtPrice}>Ext Price</TableCell>
                <TableCell width={COLUMN_WIDTHS.TAACompliant}>TAA Compliant</TableCell>
                <TableCell width={COLUMN_WIDTHS.ProductCategory}>Product Category</TableCell>
                <TableCell width={COLUMN_WIDTHS.CountryOfOrigin}>Country of Origin</TableCell>
                <TableCell width={COLUMN_WIDTHS.PoPStart}>PoP Start Date</TableCell>
                <TableCell width={COLUMN_WIDTHS.PoPEnd}>PoP End Date</TableCell>
                <TableCell width={COLUMN_WIDTHS.ServiceTag}>Service Tag</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {selectedLineItems.map(lineItem => (
                <TableRow key={lineItem.Number} className="bundle-table-row">
                  <TableCell className="bundle-radio-column" align="center">
                    <Radio checked={headerLine?.Number === lineItem.Number} onChange={() => setHeaderLine(lineItem)} />
                    <Typography variant="body1">
                      {headerLine ? (lineItem === headerLine ? 'Parent ' : 'Child ') : ''}
                    </Typography>
                    <IconButton
                      onClick={() =>
                        setSelectedLineItems(selectedLineItems.filter(item => item.Number !== lineItem.Number))
                      }
                    >
                      <DeleteOutlined />
                    </IconButton>
                  </TableCell>
                  <TableCell className="bundle-number-column" sx={{ left: COLUMN_WIDTHS.Radio }}>
                    <Typography variant="body1">{lineItem.Number}</Typography>
                  </TableCell>
                  <TableCell
                    className="bundle-supplier-column"
                    sx={{ left: `calc(${COLUMN_WIDTHS.Radio} + ${COLUMN_WIDTHS.Number})` }}
                  >
                    {lineItem.Supplier ?? '-'}
                  </TableCell>
                  <TableCell
                    className="bundle-mfr-part-number-column"
                    sx={{
                      left: `calc(${COLUMN_WIDTHS.Radio} + ${COLUMN_WIDTHS.Number} + ${COLUMN_WIDTHS.Supplier})`,
                    }}
                  >
                    {lineItem.MfrPartNum ?? '-'}
                  </TableCell>
                  <TableCell>{lineItem.Manufacturer ?? '-'}</TableCell>
                  <TableCell>{lineItem.SupplierPartNum ?? '-'}</TableCell>
                  <TableCell>{lineItem.ProductDescription ?? '-'}</TableCell>
                  <TableCell>
                    {lineItem.ShipToAddress1
                      ? `${lineItem.ShipToAttention ?? ''} ${lineItem.ShipToCompany ?? ''} ${lineItem.ShipToAddress1} ${
                          lineItem.ShipToAddress2 ?? ''
                        } ${lineItem.ShipToAddressCity ?? ''} ${lineItem.ShipToAddressState ?? ''} ${
                          lineItem.ShipToAddressZip ?? ''
                        } ${lineItem.ShipToAddressCountry ?? ''}`
                      : `${defaultAddress?.ShipToAttention ?? ''} ${defaultAddress?.ShipToCompany ?? ''} ${
                          defaultAddress?.ShipToAddress1 ?? ''
                        } ${defaultAddress?.ShipToAddress2 ?? ''} ${defaultAddress?.ShipToAddressCity ?? ''} ${
                          defaultAddress?.ShipToAddressState ?? ''
                        } ${defaultAddress?.ShipToAddressZip ?? ''} ${defaultAddress?.ShipToAddressCountry ?? ''}`}
                  </TableCell>
                  <TableCell>{lineItem.Quantity ?? '-'}</TableCell>
                  <TableCell>
                    {lineItem.UnitCost?.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) ?? '-'}
                  </TableCell>
                  <TableCell>
                    {lineItem.ExtCost?.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) ?? '-'}
                  </TableCell>
                  <TableCell>
                    {lineItem.UnitPrice?.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) ?? '-'}
                  </TableCell>
                  <TableCell>
                    {lineItem.ExtPrice?.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) ?? '-'}
                  </TableCell>
                  <TableCell>
                    {lineItem.TaaCompliant === true ? 'Yes' : lineItem.TaaCompliant === false ? 'No' : 'Not Applicable'}
                  </TableCell>
                  <TableCell>{lineItem.ProductCategory ?? '-'}</TableCell>
                  <TableCell>{lineItem.CountryOfOrigin ?? '-'}</TableCell>
                  <TableCell>
                    {lineItem.PopStartDate ? dayjs(lineItem.PopStartDate).format('MM/DD/YYYY') : '-'}
                  </TableCell>
                  <TableCell>{lineItem.PopEndDate ? dayjs(lineItem.PopEndDate).format('MM/DD/YYYY') : '-'}</TableCell>
                  <TableCell>{lineItem.ServiceTag ?? '-'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {showStickyScrollbar && (
          <div className="bundle-sticky-scrollbar" ref={stickyScrollbarRef} onScroll={() => syncScroll('sticky')}>
            <div style={{ width: stickyScrollbarWidth, height: 1 }} />
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
